import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Delete, Edit2 } from "lucide-react";
import { bankingService } from "../../services/bankingService";
import DelgroProgressBar from "../../generals/DelgroProgressBar";
import { formatCustomDate } from "../../helpers/formatCustomDate";
import { formatCustomNumber } from "../../helpers/formatCustomNumber";
import GeneralModal from "../../helpers/GeneralModal";
import { createModal } from "react-modal-promise";
import FundTransferScroll from "./FundTransferScroll";
import { SetFundTransferEditAtom } from "../../store/fundTransferAtom";

const TRANSFER_TYPE_NAMES = {
    19: "Transfer To Another Account",
    20: "Receive From Another Account",
};

function FundTransferDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const myPromiseModal = createModal(GeneralModal);
    const setFundTransferEdit = SetFundTransferEditAtom();

    const url = pathname.split('/');
    const currModuleId = url[1];
    const currModuleName = url[2];
    const currUserMenu = url[3];

    const [transferInfo, setTransferInfo] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        document.title = 'Fund Transfer Details';
        setIsLoading(true);
        bankingService.bankingTransferDetails(id).then((res) => {
            setTransferInfo(res.data?.account_info?.length ? res.data.account_info : []);
        }).finally(() => setIsLoading(false));
    }, [id]);

    const header = transferInfo[0];
    const debitRecord = transferInfo.find(r => r.debit_amount > 0) || transferInfo[0];
    const creditRecord = transferInfo.find(r => r.credit_amount > 0) || transferInfo[1];
    const { totalDebt, totalCredit } = transferInfo.reduce((acc, item) => ({
        totalDebt: acc.totalDebt + (item.debit_amount || 0),
        totalCredit: acc.totalCredit + (item.credit_amount || 0),
    }), { totalDebt: 0, totalCredit: 0 });

    const deleteTransfer = async () => {
        const result = await myPromiseModal({
            title: "Confirm Delete",
            body: `Fund transfer #${header?.account_ref_no} will be permanently deleted.`,
            showInput: false,
            placeHolder: "",
        });
        if (result?.decision !== true) return;

        if (header?.transaction_type_id === 20) {
            await bankingService.deleteReceiveFromAnotherAccount(header.account_ref_no);
        } else if (header?.transaction_type_id === 19) {
            await bankingService.deleteReceiveToAnotherAccount(header.account_ref_no);
        }
        navigate(-1);
    };

    const editTransfer = () => {
        const isReceive = header?.transaction_type_id === 20;
        const primary = isReceive ? debitRecord : creditRecord;
        const other = isReceive ? creditRecord : debitRecord;
        setFundTransferEdit({
            account_ref_no: header.account_ref_no,
            transaction_type_id: header.transaction_type_id,
            journal_date: header.journal_date,
            reference_number: header.reference_number,
            from_account_id: primary?.account_id,
            to_account_id: other?.account_id,
            currency_id: primary?.currency_id,
            amount: isReceive ? primary?.debit_amount : primary?.credit_amount,
            notes: header.notes || '',
            attachment_url: header.attachement_url || '',
        });
        navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
    };

    return (
        <section className="mt-2">
            {isLoading && <DelgroProgressBar />}

            <div className="mt-4 lg:flex space-x-1">
                <div className="lg:w-[210px]">
                    <FundTransferScroll />
                </div>

                <div className="w-full overflow-x-hidden mt-4 lg:mt-0 lg:pl-4">
                    {!isLoading && header && (
                        <div className="bg-white min-h-screen rounded-xl border border-gray-100 shadow-sm">
                            {/* Top header bar */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                                <div>
                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-0.5">
                                        Fund Transfer #{header?.account_ref_no}
                                    </p>
                                    <h1 className="text-lg font-bold text-gray-900">
                                        {TRANSFER_TYPE_NAMES[header?.transaction_type_id] || header?.transation_type_name || 'Fund Transfer Details'}
                                    </h1>
                                </div>
                                <div className="flex items-center gap-2">
                                    {header?.status_name && (
                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                            header.status_id === 2
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-yellow-100 text-yellow-700'
                                        }`}>
                                            {header.status_name}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Action buttons */}
                                <div className="flex items-center gap-5">
                                    <button onClick={() => navigate(-1)} className="text-sm text-gray-600 hover:text-gray-800 font-medium">
                                        &larr; Back
                                    </button>
                                    <button onClick={editTransfer} className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium">
                                        <Edit2 className="w-4 h-4" /> Edit
                                    </button>
                                    <button onClick={deleteTransfer} className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 font-medium">
                                        <Delete className="w-4 h-4" /> Delete
                                    </button>
                                </div>

                                {/* Total amount */}
                                <div className="bg-blue-50 border border-blue-100 rounded-xl px-5 py-4 inline-block">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">Amount</p>
                                    <p className="text-3xl font-bold text-blue-600">
                                        ₦{formatCustomNumber(debitRecord?.debit_amount || creditRecord?.credit_amount || 0)}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">on {formatCustomDate(header?.journal_date)}</p>
                                </div>

                                {/* Header meta */}
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4 text-sm">
                                    <div>
                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Reference Number</p>
                                        <p className="font-medium text-gray-800">{header?.reference_number || '—'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Journal Number</p>
                                        <p className="font-medium text-gray-800">{header?.journal_number || '—'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Encoded By</p>
                                        <p className="font-medium text-gray-800">{header?.encoded_by_name || '—'}</p>
                                    </div>
                                    <div className="col-span-2 md:col-span-3">
                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Notes</p>
                                        <p className="font-medium text-gray-800">{header?.notes || '—'}</p>
                                    </div>
                                </div>

                                {/* Debit/Credit table */}
                                <div>
                                    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Transaction Details</h2>
                                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                    <th className="px-4 py-3 text-left">Account</th>
                                                    <th className="px-4 py-3 text-right">Debit (₦)</th>
                                                    <th className="px-4 py-3 text-right">Credit (₦)</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {transferInfo.map((item, idx) => (
                                                    <tr key={item.account_id ?? idx} className="hover:bg-gray-50 transition">
                                                        <td className="px-4 py-3 font-medium text-gray-800">{item.account_name}</td>
                                                        <td className="px-4 py-3 text-right text-gray-700">
                                                            {item.debit_amount > 0 ? formatCustomNumber(item.debit_amount) : '—'}
                                                        </td>
                                                        <td className="px-4 py-3 text-right text-gray-700">
                                                            {item.credit_amount > 0 ? formatCustomNumber(item.credit_amount) : '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr className="bg-gray-50 border-t-2 border-gray-200">
                                                    <td className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                                                        Total
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-bold text-red-600">
                                                        ₦{formatCustomNumber(totalDebt)}
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-bold text-green-600">
                                                        ₦{formatCustomNumber(totalCredit)}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>

                                {header?.attachement_url && (
                                    <div>
                                        <a
                                            className="text-sm text-blue-600 hover:underline font-medium"
                                            rel="noreferrer"
                                            href={header.attachement_url}
                                            target="_blank"
                                        >
                                            Open Attachment
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {!isLoading && !header && (
                        <div className="text-center text-gray-400 py-20">No transaction found.</div>
                    )}
                </div>
            </div>
        </section>
    );
}

export default FundTransferDetailsPage;
